import { useAutosave, useServices } from "@hq/client-react";
import type { Note } from "@hq/contracts";
import { MarkdownEditor, type EditorContent, type MarkdownEditorHandle } from "@hq/markdown-editor";
import { cn, toast } from "@z/ui";
import { type ReactNode, type Ref, useEffect, useImperativeHandle, useRef, useState } from "react";
import { AddTodoPopover } from "./AddTodo";
import { lineToTodoTitle, type AddTodoRequest } from "./commands";

/** A single editor survives the transition from an empty day to a persisted note. */
export function DailyWritingSurface({
  date,
  note,
  folderId,
  label,
  autoFocus,
  heading,
  isToday,
  ref,
}: {
  date: string;
  note: Note | undefined;
  folderId: string | undefined;
  label: string;
  autoFocus: boolean;
  heading: ReactNode;
  isToday: boolean;
  ref: Ref<{ focus(): void }>;
}) {
  const services = useServices();
  const autosave = useAutosave();
  const editor = useRef<MarkdownEditorHandle>(null);
  const id = useRef(note?.id);
  const draft = useRef<EditorContent | null>(null);
  const creating = useRef(false);
  const [request, setRequest] = useState<(AddTodoRequest & { sourceNoteId: string }) | null>(null);
  useImperativeHandle(ref, () => ({ focus: () => editor.current?.focus() }));
  useEffect(() => {
    if (note) id.current = note.id;
  }, [note]);
  const flush = () => {
    if (id.current) void autosave.flush(id.current);
  };
  useEffect(() => {
    const save = () => {
      if (id.current) void autosave.flush(id.current);
    };
    const visibility = () => {
      if (document.visibilityState === "hidden") save();
    };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("pagehide", save);
    return () => {
      save();
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("pagehide", save);
    };
  }, [autosave]);
  const change = (value: EditorContent, baseRevision?: number) => {
    draft.current = value;
    if (id.current) {
      autosave.update(id.current, value, baseRevision);
      return;
    }
    if (creating.current || !value.markdown) return;
    creating.current = true;
    void services.notes
      .openDaily(date, folderId)
      .then((created) => {
        id.current = created.id;
        creating.current = false;
        // Save the latest draft, including keystrokes received during note creation.
        if (draft.current) autosave.update(created.id, draft.current, created.revision);
        void autosave.flush(created.id);
      })
      .catch(() => {
        creating.current = false;
        toast({
          title: "Could not save this day",
          description: "Your draft is still here. Keep typing to retry.",
          variant: "danger",
        });
      });
  };
  return (
    <>
      <MarkdownEditor
        ref={editor}
        documentId={note?.id ?? `daily-${date}`}
        value={{ document: note?.document, markdown: note?.markdown ?? "" }}
        revision={note?.revision}
        onChange={change}
        onBlur={flush}
        label={label}
        autoFocus={autoFocus}
        toolbar={false}
        toolbarStart={heading}
        placeholder="Write about your day…"
        className="gap-3 text-base"
        contentClassName={cn(
          "[&_.ze-content]:!py-0",
          isToday ? "[&_.ze-content]:min-h-24" : "[&_.ze-content]:min-h-12",
        )}
        acceptExternalChange={() =>
          !creating.current &&
          (id.current
            ? ["idle", "saved"].includes(autosave.getState(id.current))
            : draft.current === null)
        }
        onAddTodo={(s) => {
          void services.notes
            .openDaily(date, folderId)
            .then((source) => {
              setRequest({
                sourceNoteId: source.id,
                lineText: s.line,
                selectionText: s.text,
                title: lineToTodoTitle(s.text || s.line),
                coords: s.coords,
              });
            })
            .catch(() => toast({ title: "Could not open this daily note", variant: "danger" }));
        }}
      />
      {request && (
        <AddTodoPopover
          sourceNoteId={request.sourceNoteId}
          request={request}
          onClose={() => {
            setRequest(null);
            editor.current?.focus();
          }}
        />
      )}
    </>
  );
}
